import React, { useEffect, useRef, useState } from "react";
import { Alert, Card, CardBody, Col, Container, Row } from "reactstrap";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { getSubscription, Subscription } from "../../helpers/subscription_helper";

/**
 * Soft-lock billing — suspended/canceled só acessam pagamento + dashboard + conta.
 * Inclui empty de fallback (se redirect falhar) + toast ao voltar para active.
 */
const ALLOWED_PREFIXES = [
  "/suspended",
  "/settings/subscription",
  "/settings/account",
  "/dashboard",
  "/index",
];

const PREV_STATUS_KEY = "brenvio.billing.prevStatus";

function isAllowedPath(pathname: string): boolean {
  if (pathname === "/" || pathname === "") {
    return true;
  }
  return ALLOWED_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );
}

export function isBillingSoftLocked(status: string | undefined | null): boolean {
  return status === "suspended" || status === "canceled";
}

/** Empty operacional — Carla entende o bloqueio sem JSON/código. */
export const BillingOperationalEmpty = ({
  status,
}: {
  status?: string | null;
}) => {
  const { t } = useTranslation();
  const isSuspended = status === "suspended";

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="justify-content-center">
          <Col lg={7} md={9}>
            <Card className="mt-4">
              <CardBody className="text-center py-5 px-4">
                <Alert
                  color={isSuspended ? "warning" : "danger"}
                  className="text-start mb-4"
                >
                  <strong className="d-block mb-1">
                    {t(
                      isSuspended
                        ? "billing.softLock.suspended.title"
                        : "billing.softLock.canceled.title"
                    )}
                  </strong>
                  {t(
                    isSuspended
                      ? "billing.softLock.suspended.description"
                      : "billing.softLock.canceled.description"
                  )}
                </Alert>
                <Link
                  to="/settings/subscription?focus=payment"
                  className="btn btn-primary"
                >
                  {t("billing.softLock.ctaPlan")}
                </Link>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

type Props = {
  children: React.ReactNode;
};

const BillingSoftLockGuard = ({ children }: Props) => {
  const location = useLocation();
  const { t } = useTranslation();
  const [subscription, setSubscription] = useState<
    Subscription | null | undefined
  >(undefined);
  const toastedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getSubscription();
        if (!cancelled) {
          setSubscription(res.data ?? null);
        }
      } catch {
        if (!cancelled) {
          setSubscription(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  // Toast ao reativar: past_due/suspended → active (sessão anterior).
  useEffect(() => {
    if (!subscription?.status || toastedRef.current) {
      return;
    }
    const prev = sessionStorage.getItem(PREV_STATUS_KEY);
    const cur = subscription.status;
    if (
      prev &&
      (prev === "past_due" || prev === "suspended") &&
      cur === "active"
    ) {
      toast.success(t("billing.reactivated.toast"), { autoClose: 5000 });
      toastedRef.current = true;
    }
    sessionStorage.setItem(PREV_STATUS_KEY, cur);
  }, [subscription?.status, t]);

  if (subscription === undefined) {
    return <>{children}</>;
  }

  if (
    isBillingSoftLocked(subscription?.status) &&
    !isAllowedPath(location.pathname)
  ) {
    // Navigate + empty de segurança se o soft-lock “vazar” na rota.
    return (
      <>
        <Navigate to="/suspended" replace />
        <BillingOperationalEmpty status={subscription?.status} />
      </>
    );
  }

  return <>{children}</>;
};

export default BillingSoftLockGuard;
